import { gettext } from '@eventbrite/i18n';
import interpolate from '@eventbrite/interpolate-lazy-string';
import { formatMajorMoney, formatNumber } from '@eventbrite/intl';
import simmut from 'simmut';

import paymentConstraintsHelper from '../../helpers/paymentConstraints-helpers';
import memoize from '../../helpers/memoize';
import timestamp from '../../helpers/timestamp';
import {
    STATUS_AVAILABLE,
    STATUS_CARTED,
    STATUS_LOCKED,
    STATUS_PROTECTED,
    STATUS_RELEASING,
    STATUS_REQUESTING,
    STATUS_RESERVED,
} from '../../constants/status-constants';
import { TICKET_CLASS_CATEGORY_ADDON } from '../../constants/tickets-constants';
import geometryHelper from '../../helpers/geometry-helper';
import ticketHelper from '../../helpers/ticket-helper';
import {
    LOOKAT_MARGIN,
    SCREEN_CENTER_TARGET_X,
    SCREEN_CENTER_TARGET_Y,
    VIEW_MODE_MANAGE_DASHBOARD,
    VIEW_MODE_PAS,
} from '../../constants/global-constants';
import sFetch from '../../simple-flow/s-fetch';
import ebUrl from '../../simple-flow/eb-url';
import paginationHelper from '../../services/event-service/pagination-helper';

const { aFetch } = sFetch;

const { getApiUrl } = ebUrl;

const { mergeTaxOptions } = paginationHelper;

const { getBoxFromPositions } = geometryHelper;

const { getTicketDataFromVariantId, getVariantsFromTier } = ticketHelper;

const MAX_TICKET_EXCEED_ERROR = gettext(
    'You cannot exceed the %(numberOfTickets)s ticket limit for %(ticketName)s.',
);

const pasManagerFactory = ({
    aLookAt,
    aSetPromoCode,
    cartService,
    eventService,
    getEventIdAndCartId,
    getPageParams,
    getPositionFromIds,
    getTierFromId,
    model,
    setErrorMessage,
    setOrderData,
    setSuccessMessage,
    selectSeats,
    aShowTicketSelection,
    trackAnalyticsAction,
    getViewMode,
}) => {
    const {
        notify: cartServiceNotify,
        off: cartServiceOff,
        on: cartServiceOn,
    } = cartService;

    const {
        aRetrieveAttendees: eventServiceARetrieveAttendees,
        aSetAttendees: eventServiceASetAttendees,
        aSetPaymentInstruction: eventServiceASetPaymentInstruction,
        aReleaseHold: eventServiceAReleaseHold,
        aRetrieveHoldsData: eventServiceARetrieveHoldsData,
        aRetrieveSoldSeatInfo: eventServiceARetrieveSoldSeatInfo,
        aUpdateEventHolds: eventServiceAUpdateEventHolds,
    } = eventService;

    const _getServerStatus = ({ id = '' }) => {
        const parts = id.split(':') || '';
        const rowId = parts[0];
        const index = +parts[1] - 1;

        let value;

        // eslint-disable-next-line no-restricted-globals
        if (!isNaN(index)) {
            const serverStatus = model.get(`status.${rowId}.s`) || [];

            value = serverStatus[index];
        }
        return value;
    };

    const _isReservedSeatingUnit = (id) => id.indexOf(':') >= 0;

    const getFullStatusFromId = ({ id }) => {
        if (!id) {
            throw new Error('Please provide id');
        }
        const isReservedSeatingUnit = _isReservedSeatingUnit(id);
        return isReservedSeatingUnit
            ? _getFullReservedSeatingUnitStatusFromId({
                  id,
              })
            : _getFullGASectionStatusFromId({ id });
    };

    const _getFullReservedSeatingUnitStatusFromId = ({ id }) => {
        let unitStatus = STATUS_LOCKED;

        const serverStatus = _getServerStatus({ id }) || STATUS_LOCKED;
        const selectedStatus = model.get(`globals.cart.${id}.status`);
        const statusTs = model.get('globals.statusTs') || 0;
        const selectedTs = model.get(`globals.cart.${id}.ts`) || 0;

        unitStatus = serverStatus;

        if (selectedStatus === STATUS_CARTED || selectedTs > statusTs) {
            unitStatus = selectedStatus;
        }

        return unitStatus;
    };

    const _getFullGASectionStatusFromId = ({ id }) => {
        let gaStatus = STATUS_LOCKED;

        // This is a GA section as we do not have a ":" in the id
        // For GA sections we return the status of the section, not of the unit
        // because we are selecting the whole section
        const sectionStatus = model.get(`status.${id}.S`) || {};
        const availableTickets = sectionStatus.A;
        const protectedTickets = sectionStatus.P;

        if (protectedTickets) {
            gaStatus = STATUS_PROTECTED;
        } else if (availableTickets) {
            gaStatus = STATUS_AVAILABLE;
        }

        return gaStatus;
    };

    const _getEmptyOrder = () => ({
        order: {
            attendees: [],
            order: {
                attendees: [],
                costs: {
                    base_price: {
                        display: '€0.00',
                        currency: 'EUR',
                        value: 0,
                        major_value: '0.00',
                    },
                    eventbrite_fee: {
                        display: '€0.00',
                        currency: 'EUR',
                        value: 0,
                        major_value: '0.00',
                    },
                    gross: {
                        display: '€0.00',
                        currency: 'EUR',
                        value: 0,
                        major_value: '0.00',
                    },
                    payment_fee: {
                        display: '€0.00',
                        currency: 'EUR',
                        value: 0,
                        major_value: '0.00',
                    },
                    tax: {
                        display: '€0.00',
                        currency: 'EUR',
                        value: 0,
                        major_value: '0.00',
                    },
                    fee_components: [],
                    shipping_components: [],
                    tax_components: [],
                },
            },
        },
    });

    const aLookAtAttendees = async ({ attendeesNode }) => {
        if (attendeesNode && attendeesNode.length) {
            // First we set as selected all attendees which are in the cart
            // and whose ts is bigger than the selection that we have in the UI
            const positions = [];

            for (let i = 0; i < attendeesNode.length; i++) {
                const attendee = attendeesNode[i];
                const unitId = simmut.get(attendee, 'assigned_unit.unit_id');

                if (unitId) {
                    const position = getPositionFromIds({ ids: [unitId] });

                    if (position) {
                        positions.push(position);
                    }
                }
            }
            if (positions.length) {
                const targetViewBox = getBoxFromPositions({
                    positions,
                });

                await aLookAt({
                    targetViewBox,
                    margin: LOOKAT_MARGIN,
                    targetPositionInNormalizedViewCoordinates: {
                        x: SCREEN_CENTER_TARGET_X,
                        y: SCREEN_CENTER_TARGET_Y,
                    },
                    autoZoom: true,
                });
            }
        }
    };

    const setCartFromAttendees = ({ attendeesNode, ts }) => {
        const attendeesTs = ts || timestamp();

        //attendees map
        const atMap = {};
        const gaNode = {};
        const usingHold = model.get('globals.usingHold');
        const holdIds = model.get('holds.ids') || {};
        const addonsFromAttendees = {};

        if (attendeesNode && attendeesNode.length) {
            // First we set as selected all attendees which are in the cart
            // and whose ts is bigger than the selection that we have in the UI

            for (let i = 0; i < attendeesNode.length; i++) {
                const unitId = simmut.get(
                    attendeesNode,
                    `${i}.assigned_unit.unit_id`,
                );
                const variantId = simmut.get(attendeesNode, `${i}.variant_id`);

                if (unitId) {
                    if (unitId.includes(':')) {
                        const selectedTs =
                            model.get(`globals.cart.${unitId}.ts`) || 0;

                        atMap[unitId] = true;
                        if (!selectedTs || attendeesTs > selectedTs) {
                            model.set(
                                `globals.cart.${unitId}.status`,
                                STATUS_CARTED,
                            );
                            model.set(`globals.cart.${unitId}.ts`, attendeesTs);
                            model.set(
                                `globals.cart.${unitId}.variant_id`,
                                variantId,
                            );
                        }
                    } else {
                        // This is a GA ticket
                        const ticketsFromGA = gaNode[unitId] || [];

                        ticketsFromGA.push({
                            ts,
                            variant_id: variantId,
                        });
                        gaNode[unitId] = ticketsFromGA;
                    }
                } else {
                    // We have to handle add-ons
                    let addon = addonsFromAttendees[variantId];

                    if (!addon) {
                        addon = {
                            qty: 0,
                            ts,
                        };
                        addonsFromAttendees[variantId] = addon;
                    }
                    addon.qty++;
                }
            }
            const localAddons = model.get('globals.addons') || {};
            // We set the addons to be the ones coming in the cart from the server

            model.set('globals.addons', addonsFromAttendees);
            Object.keys(localAddons).forEach((variantId) => {
                const { qty: localQty, ts: localTs } = localAddons[variantId];

                // If the local addon qty was modified after the server call,
                // we will overwrite the qty in the cart. This will effectively delete
                // addons no longer in use (qty 0 in the local map, but set before the sync with the server)
                if (localTs > ts) {
                    model.set(`globals.addons.${variantId}.qty`, localQty);
                    model.set(`globals.addons.${variantId}.ts`, localTs);
                }
            });
        }
        // Now we will remove all seats which are not in the attendeesMap and whose ts is smaller
        // than the attendees ts
        const cart = model.get('globals.cart') || {};

        Object.keys(cart).forEach((unitId) => {
            const selectedChairRecord = cart[unitId];

            const { status: unitStatus, ts: unitTs } = selectedChairRecord;

            if (
                unitStatus === STATUS_CARTED ||
                unitStatus === STATUS_REQUESTING ||
                unitStatus === STATUS_RELEASING
            ) {
                // If the ts (timestamp) of the chair is smaller than the ts of the attendees
                // and the attendee does not exist, we have to remove that chair from the
                // selected list.
                if (!unitTs || attendeesTs > unitTs) {
                    if (!atMap[unitId]) {
                        let releaseStatus = STATUS_AVAILABLE;

                        if (usingHold) {
                            releaseStatus = STATUS_PROTECTED;
                        }

                        if (holdIds[unitId]) {
                            releaseStatus = STATUS_RESERVED;
                        }

                        if (unitStatus === STATUS_REQUESTING) {
                            releaseStatus = STATUS_LOCKED;
                        }

                        model.set(
                            `globals.cart.${unitId}.status`,
                            releaseStatus,
                        );
                        model.set(`globals.cart.${unitId}.ts`, attendeesTs);
                    }
                }
            }
        });
        model.set('globals.cartGA', gaNode);

        // If constrainedInstrumentType is undefined, then we are currently initializing
        // the seat map and need to evaluate the selected chairs for payment constraints
        // and set the constrainedInstrumentType and constrainedPaymentMethods on global
        // state.
        if (model.get('globals.constrainedInstrumentType') === undefined) {
            const ticketClassesNode = getTicketClasses();
            const cartNode = model.get('globals.cart');

            if (cartNode) {
                const chairs = Object.keys(cartNode);

                const selectedTickets = chairs.map((unitId) => {
                    const chairVariantId = simmut.get(
                        cartNode,
                        `${unitId}.variant_id`,
                    );
                    const ticketClassNode = ticketClassesNode.find(
                        (ticketNode) =>
                            chairVariantId ===
                            simmut.get(ticketNode, 'variant_id'),
                    );
                    const chairPaymentConstraints =
                        simmut.get(ticketClassNode, 'payment_constraints') ||
                        [];

                    let chairInstrumentType = null;
                    let chairPaymentMethods = null;

                    chairPaymentMethods = chairPaymentConstraints.map(
                        (constraint) => {
                            if (!chairInstrumentType) {
                                chairInstrumentType = simmut.get(
                                    constraint,
                                    'instrument_type',
                                );
                            }

                            const paymentMethod = simmut.get(
                                constraint,
                                'payment_method',
                            );

                            return paymentMethod
                                ? paymentMethod
                                : chairInstrumentType;
                        },
                    );

                    return {
                        instrument_type: chairInstrumentType,
                        payment_methods: chairPaymentMethods,
                        unit_id: unitId,
                        variant_id: chairVariantId,
                    };
                });

                paymentConstraintsHelper.findCommonPaymentConstraints({
                    model,
                    tickets: selectedTickets,
                });
                paymentConstraintsHelper.checkTicketsForConflictingPayment({
                    model,
                });
            }
        }
    };

    const aSyncCartFromServer = async () => {
        const { cartId, eventId } = getEventIdAndCartId();
        const ts = timestamp();

        const cartResult = await eventServiceARetrieveAttendees({
            eventId,
            cartId,
        }).catch((e) => {
            if (
                e instanceof TypeError &&
                e.message.includes('Failed to fetch')
            ) {
                return {
                    attendees: [],
                    order: {},
                };
            }
        });

        if (cartResult && cartResult.attendees) {
            const attendeesModel = cartResult.attendees;

            setCartFromAttendees({ attendeesNode: attendeesModel, ts });
        }
    };

    const aSetAttendees = async ({ tickets }) => {
        if (!tickets) {
            setErrorMessage({
                msg: gettext(
                    'There was an error selecting the seat. Please try again later.',
                ),
                transient: true,
            });
            throw new Error('No tickets provided');
        }
        const { cartId, eventId } = getEventIdAndCartId();
        cartServiceNotify({
            args: tickets,
            type: 'cart:item:willLock',
        });
        const ts = timestamp();

        const promoCode = model.get('globals.promoCode');
        const deliveryMethodOrder = model.get('globals.deliveryMethodOrder');
        let result = undefined;

        try {
            result = await eventServiceASetAttendees({
                cartId,
                deliveryMethodOrder,
                eventId,
                promoCode,
                tickets,
            });
        } catch (e) {
            if (
                e instanceof TypeError &&
                e.message.includes('Failed to fetch')
            ) {
                aSyncCartFromServer({ eventId, cartId });
                cartServiceNotify({
                    args: [_getEmptyOrder()],
                    type: 'cart:item:didLock',
                });
                cartServiceNotify({
                    args: [],
                    type: 'cart:doneUpdating',
                });
            }
        }

        let success = false;

        if (result && result.attendees) {
            const attendeesModel = result.attendees;

            paymentConstraintsHelper.findCommonPaymentConstraints({
                model,
                tickets,
            });
            paymentConstraintsHelper.checkTicketsForConflictingPayment({
                model,
            });

            const params = {
                constrained_instrument_type: model.get(
                    'globals.constrainedInstrumentType',
                ),
                constrained_payment_methods: model.get(
                    'globals.constrainedPaymentMethods',
                ),
                order: result,
            };

            model.set('globals.attendees', attendeesModel);
            success = true;
            if (result.order) {
                setOrderData(result.order);
            }
            setCartFromAttendees({ attendeesNode: attendeesModel, ts });
            cartServiceNotify({
                args: [params],
                type: 'cart:item:didLock',
            });
        } else {
            success = false;

            if (
                result?.status_code === 400 &&
                result?.error === 'INVENTORY_ERROR' &&
                result?.error_description?.EXCEED_QTY_REMAINING
            ) {
                setErrorMessage({
                    msg: gettext(
                        'One or more tickets selected exceed the ticket limit for that type.',
                    ),
                    transient: false,
                });
            }

            await aSyncCartFromServer({ eventId, cartId });
        }
        cartServiceNotify({
            args: [],
            type: 'cart:doneUpdating',
        });

        return success;
    };

    const _mapCostToGross = ({ value, currency }) => ({ value, currency });
    const _getTicketGross = (variantId, holdId) => {
        const ticketClass = getTicketClasses().find(
            ({ variant_id }) =>
                variant_id === variantId ||
                variant_id === `${variantId}-H${holdId}`,
        );

        const gross =
            ticketClass &&
            (getViewMode() === VIEW_MODE_MANAGE_DASHBOARD
                ? ticketClass.gross ||
                  _mapCostToGross(
                      ticketClass.cost || {
                          value: 0,
                          currency: getEventCurrency(),
                      },
                  )
                : ticketClass.gross);

        return gross;
    };

    const _getNewCart = () => {
        const cart = model.get('globals.cart') || {};
        const holdIds = model.get('holds.ids') || {};
        const pickedIds = Object.keys(cart) || [];
        const pickedTickets = pickedIds.reduce((cart, unitId) => {
            const variantId = model.get(`globals.cart.${unitId}.variant_id`);
            const unitStatus = model.get(`globals.cart.${unitId}.status`);
            const holdId = holdIds[unitId];

            const gross = _getTicketGross(variantId, holdId);

            const unitIdToUse = holdId ? `${unitId}h${holdId}` : unitId;

            if (variantId) {
                switch (unitStatus) {
                    case STATUS_CARTED:
                    case STATUS_REQUESTING:
                        cart.push({
                            unit_id: unitIdToUse,
                            variant_id: variantId,
                            ...(gross && { gross }),
                        });
                        break;
                    default:
                }
            }
            return cart;
        }, []);

        const cartGA = model.get('globals.cartGA') || {};
        const cartGAIds = Object.keys(cartGA) || [];
        const ticketsWithGA = cartGAIds.reduce((cart, gaId) => {
            const cartToReturn = cart;
            const tickets = cartGA[gaId];

            if (tickets && tickets.length) {
                tickets.forEach((ticket) => {
                    const gross = _getTicketGross(ticket['variant_id']);

                    cartToReturn.push({
                        unit_id: gaId,
                        variant_id: ticket['variant_id'],
                        ...(gross && { gross }),
                    });
                });
            }
            return cartToReturn;
        }, pickedTickets);

        const addons = model.get('globals.addons') || {};

        const addonTickets = [];

        Object.keys(addons).forEach((variantId) => {
            const { qty } = addons[variantId];

            for (let i = 0; i < qty; i++) {
                addonTickets.push({ variant_id: variantId });
            }
        });

        return [...ticketsWithGA, ...addonTickets].map((ticket) => {
            const { variant_id: variantId } = ticket;
            const ticketClasses = getTicketClasses();
            const selectedTicketClass = ticketClasses.find(
                (ticketClass) =>
                    simmut.get(ticketClass, 'variant_id') === variantId,
            );
            const paymentConstraints =
                simmut.get(selectedTicketClass, 'payment_constraints') || [];
            let instrumentType = null;
            let paymentMethods = null;

            if (paymentConstraints.length) {
                paymentMethods = paymentConstraints.map((constraint) => {
                    if (!instrumentType) {
                        instrumentType = simmut.get(
                            constraint,
                            'instrument_type',
                        );
                    }

                    const paymentMethod = simmut.get(
                        constraint,
                        'payment_method',
                    );

                    return paymentMethod ? paymentMethod : instrumentType;
                });
            }

            return {
                ...ticket,
                instrument_type: instrumentType,
                payment_methods: paymentMethods,
            };
        });
    };

    const setSyncCartStatus = (value) =>
        model.set('globals.syncCartIsLoading', value);

    const getSyncCartStatus = () => model.get('globals.syncCartIsLoading');

    const getLegendDialogVisibility = () =>
        model.get('globals.legendDialogIsShowing');

    const toggleLegendDialogVisibility = () => {
        model.set('globals.printDialogIsShowing', false);
        model.set(
            'globals.legendDialogIsShowing',
            !getLegendDialogVisibility(),
        );
    };

    const hideLegendDialog = () =>
        model.set('globals.legendDialogIsShowing', false);

    const getCheckoutModalVisibility = () =>
        model.get('globals.checkoutModalIsShowing');

    const setCheckoutModalVisibility = (value) =>
        model.set('globals.checkoutModalIsShowing', value);

    let _busy = false;
    let _pending = [];

    const _getNewCartWithPricesUpdated = async () => {
        const { cartId, eventId } = getEventIdAndCartId();
        const { attendees } = await eventServiceARetrieveAttendees({
            eventId,
            cartId,
        });
        const tickets = _getNewCart();

        const _getTaxOptions = async (eid) => {
            const params = {
                url: getApiUrl(
                    `/v3/events/${eid}/tax_options/?expand=tax_policy&asset_type=ticket`,
                ),
                type: 'json',
                options: {
                    pagination: {
                        enabled: true,
                        fetchAll: true,
                    },
                    method: 'GET',
                    credentials: 'include',
                },
            };

            let parsedResult;
            try {
                const result = await aFetch(params);
                if (result.error) {
                    const {
                        error_description:
                            errorDescription = 'Something went wrong',
                        error_detail: errorDetail,
                        error,
                    } = result;
                    throw new Error(errorDescription, {
                        cause: errorDetail || error,
                    });
                }
                parsedResult = mergeTaxOptions(result)[0];
            } catch (e) {
                throw new Error(e);
            }

            return parsedResult;
        };

        let taxOptions = {};

        try {
            taxOptions = await _getTaxOptions(eventId);
        } catch (e) {
            setErrorMessage({
                msg: gettext(
                    'There was an error selecting the seat. Please try again later.',
                ),
                transient: true,
            });
            return;
        }

        const { include_tax_into_price: includeTaxIntoPrice } = taxOptions;
        const ticketsWithPriceUpdated = tickets.map((ticket) => {
            const getAttendeeByVariantIdWithPriceUpdated = ({
                variant_id: attendeeVariantId,
                costs,
                ticket_class: ticketClass,
            }) => {
                const {
                    gross: { value: grossValue },
                    tax: { value: taxValue },
                } = costs;
                const { cost: { value: ticketClassValue = 0 } = {} } =
                    ticketClass;
                let realGrossValue = grossValue;

                if (!includeTaxIntoPrice) {
                    realGrossValue = grossValue - taxValue;
                }

                return (
                    ticket.variant_id === attendeeVariantId &&
                    realGrossValue !== ticketClassValue
                );
            };

            const attendeeWithPriceUpdated =
                attendees &&
                attendees.find(getAttendeeByVariantIdWithPriceUpdated);

            let gross = ticket.gross;

            if (attendeeWithPriceUpdated && !ticket.gross) {
                gross = {
                    value: attendeeWithPriceUpdated.costs.gross.value,
                    currency: getEventCurrency(),
                };
            }
            return {
                ...ticket,
                ...(gross && { gross }),
            };
        });

        return ticketsWithPriceUpdated;
    };

    // Sync cart.
    // It can only do one sync at a time, so we return a promise
    // If we receive a call while syncing, we store the resolve function
    // into an array which will be called when it has been fulfilled.
    const aSyncCart = async () =>
        //FIXME the argument to new Promise probably shouldn't be an async function
        //eslint-disable-next-line no-async-promise-executor
        new Promise(async (resolve) => {
            _pending.push(resolve);
            if (_busy) {
                return;
            }
            _busy = true;
            setSyncCartStatus(_busy);
            const toNotify = _pending;

            _pending = [];
            const viewMode = model.get('globals.viewMode');
            const tickets =
                viewMode === VIEW_MODE_MANAGE_DASHBOARD
                    ? await _getNewCartWithPricesUpdated()
                    : await _getNewCart();

            aSetAttendees({ tickets })
                .then(() => {
                    try {
                        toNotify.forEach((fnc) => {
                            fnc();
                        });
                    } finally {
                        _busy = false;
                        setSyncCartStatus(_busy);
                        if (_pending && _pending.length) {
                            aSyncCart();
                        }
                    }
                })
                .catch((e) => {
                    model.set('globals.cart', {});
                });
        });

    const isCartExpired = () => {
        const ts = model.get('globals.order.ts');
        const timeRemaining = model.get('globals.order.time_remaining');

        if (ts && timeRemaining) {
            const expireTime = ts + timeRemaining * 1000;
            const now = Date.now();

            if (now > expireTime) {
                return true;
            }
        }
        return false;
    };

    const maxReachedForVariant = ({ variantId, quantity }) => {
        const { overrideTicketQuantities } = getPageParams();

        const viewMode = getViewMode();

        // If we are not in PAS mode, max ticket quantities do not apply
        if (viewMode !== VIEW_MODE_PAS) {
            return false;
        }
        // If we receive the param override_ticket_quantities through the URL, then we do not have
        // to limit max tickets.
        if (overrideTicketQuantities) {
            return false;
        }

        const cart = _getNewCart();

        const ticketClasses = getTicketClasses();

        const { maximumQuantityWithoutPending } = getTicketDataFromVariantId({
            ticketClasses,
            variantId,
        });

        if (maximumQuantityWithoutPending) {
            let unitsBooked = 0;

            for (let i = 0; i < cart.length; i++) {
                const record = cart[i];

                if (record['variant_id'] === variantId) {
                    unitsBooked++;
                }
            }

            if (unitsBooked + quantity > maximumQuantityWithoutPending) {
                return true;
            }
        }

        return false;
    };

    const isSeatLocked = ({ id }) => {
        const usingHold = model.get('globals.usingHold');
        const unitStatus = getFullStatusFromId({ id });
        const tier = getTierFromId({ id });
        const globals = model.get('globals');
        const variants = getVariantsFromTier({ globals, tier });
        const viewMode = getViewMode();

        if (!variants || !variants.length) {
            return true;
        }

        switch (unitStatus) {
            case STATUS_LOCKED:
                return true;
            case STATUS_RESERVED:
                return viewMode === VIEW_MODE_PAS;
            case STATUS_AVAILABLE:
                if (usingHold) {
                    return true;
                }
                return false;
            case STATUS_PROTECTED:
                if (usingHold || viewMode === VIEW_MODE_MANAGE_DASHBOARD) {
                    return false;
                }
                return true;
            case STATUS_CARTED:
            case STATUS_REQUESTING:
            case STATUS_RELEASING:
                return false;
            default:
                return true;
        }
    };

    const getMaxTicketError = ({ variantId }) => {
        const ticketClasses = getTicketClasses();
        const { maximumQuantityWithoutPending, ticketName } =
            getTicketDataFromVariantId({
                ticketClasses,
                variantId,
            });

        const str = interpolate(
            MAX_TICKET_EXCEED_ERROR,
            {
                numberOfTickets: maximumQuantityWithoutPending,
                ticketName,
            },
            true,
        );

        return str;
    };

    const _isTicketVisibleOrInPASMode = () => {
        const visible = model.get('interactive.ticketSelection.visible');
        const viewMode = getViewMode();
        return visible || viewMode === VIEW_MODE_PAS;
    };

    const _addGATicket = ({ selectedSeats = [] }, tierVariants) => {
        const id = selectedSeats[0].id;

        if (!id) {
            return;
        }

        const sectionStatus = getFullStatusFromId({ id });
        const tier = getTierFromId({ id });

        if (sectionStatus === STATUS_LOCKED) {
            return;
        }

        const variants = selectedSeats.reduce((acc, { variantId }) => {
            if (variantId) {
                if (acc[variantId]) {
                    acc[variantId]++;
                } else {
                    acc[variantId] = 1;
                }
            }

            return acc;
        }, {});

        if (!Object.entries(variants).length && tierVariants.length === 1) {
            variants[tierVariants[0].variantId] = selectedSeats.length;
        }

        const variantExceedsQuantity = Object.keys(variants).find((variantId) =>
            maxReachedForVariant({
                variantId: variantId,
                quantity: variants[variantId],
            }),
        );

        if (variantExceedsQuantity) {
            setErrorMessage({
                msg: gettext(
                    'One or more tickets selected exceed the ticket limit for that type.',
                ),
                transient: false,
            });
            return;
        }

        const alias = model.get(`status.${id}.alias`);
        const usingHold = model.get('globals.usingHold');

        let idToUse = id;

        if (alias) {
            idToUse = alias;
        }

        if (usingHold && sectionStatus !== STATUS_PROTECTED) {
            return;
        }
        if (!usingHold && sectionStatus !== STATUS_AVAILABLE) {
            return;
        }
        if (tier !== undefined) {
            const globals = model.get('globals');
            const tierVariants = getVariantsFromTier({ globals, tier });

            if (tierVariants && tierVariants.length) {
                let variantsToUse = variants;

                const isTicketVisibleOrInPASMode =
                    _isTicketVisibleOrInPASMode();

                if (
                    Object.keys(variants).length === 0 &&
                    tierVariants.length === 1 &&
                    isTicketVisibleOrInPASMode
                ) {
                    variantsToUse = {
                        [tierVariants[0].variantId]: 1,
                    };
                }

                if (
                    Object.keys(variantsToUse).length &&
                    isTicketVisibleOrInPASMode
                ) {
                    let currentTickets =
                        model.get(`globals.cartGA.${idToUse}`) || [];
                    const ts = timestamp();

                    const newCurrentTickets = Object.keys(variantsToUse).reduce(
                        (acc, variantIdToUse) => {
                            for (
                                let i = 0;
                                i < variantsToUse[variantIdToUse];
                                i++
                            ) {
                                acc.push({ ts, variant_id: variantIdToUse });
                            }

                            return acc;
                        },
                        [],
                    );

                    currentTickets = [...currentTickets, ...newCurrentTickets];
                    model.set(`globals.cartGA.${idToUse}`, currentTickets);
                    aShowTicketSelection({
                        visible: false,
                        ids: [id],
                    });
                    trackAnalyticsAction({ action: 'lockseat' });
                    aSyncCart();
                } else {
                    aShowTicketSelection({
                        ids: [id],
                        visible: true,
                    });
                }
            }
        }
    };

    const _removeGATicket = ({ selectedSeats = [] }) => {
        const id = selectedSeats[0].id;
        const variantId = selectedSeats[0].variantId;

        if (!id || !variantId) {
            return;
        }

        const currentTickets = [...model.get(`globals.cartGA.${id}`)] || [];

        if (currentTickets && currentTickets.length) {
            const indexTicketToRemove = currentTickets.findIndex(
                (ticket) => ticket['variant_id'] === variantId,
            );

            if (indexTicketToRemove >= 0) {
                currentTickets.splice(indexTicketToRemove, 1);
                model.set(`globals.cartGA.${id}`, currentTickets);
                trackAnalyticsAction({ action: 'releaseSeat' });
                aSyncCart();
            }
        }
    };

    const getSeatType = ({ id }) => {
        const seatProperties =
            model.get(`objects.contents.${id}.properties`) || {};

        return seatProperties.type;
    };

    /*
    HOW SEAT PICKING WORKS

    We have two structures

    a) globals.cart
    b) status

    globals.cart contain the current UI status of the chairs
    status contain the server status of the chairs

    Pick a seat must synchronize both structures.

    - Adding seats to the cart
    - Removing seats from the cart
    - Removing "picked" seats if they have not been secured in the cart.

    This is the algorithm

    1. User picks a seat

    pickASeat function

    2. We change the chair status property (globals.cart.{id}.status)
    3. We set the timestamp for the action in (globals.cart.{id}.ts)
    4. We call the function aSyncCart

    aSyncCart function

    5. We wait until the last 'sync' call to the server returns. We can only have one sync request to the server active at a time
    6. We create a new cart gathering all picked seats from globals.cart
    7. We call the server setAttendees
    8. We wait for response
    9. When response comes, we must delete the chairs which are "picked" but do not appear in the cart
        - But careful!: Only those whose timeStamp is smaller than the timeStamp of the moment when we launched the request


    RACE CONDITIONS

    We have a race condition when we keep selecting seats while the call to the server to set the cart is running.
    When the response comes, we would be "unpicking" seats which were selected after the call was made.

    In order to avoid that, every picked seat has a timestamp on when it was picked and the cart itself has a timestamp on when it was requested.
    Seats which were selected and do not appear on the cart should be removed from the selected list, BUT ONLY IF THE PICK TIMESTAMP is smaller than
    then timestamp of the request itself.

    */

    // eslint-disable-next-line complexity
    const pickSeats = ({ selectedSeats = [], value }) => {
        if (isCartExpired()) {
            setErrorMessage({
                msg: gettext('Your order has expired.'),
                transient: true,
            });

            return;
        }

        // If we do not have any seat, nothing to do
        if (!selectedSeats.length) {
            return;
        }

        const selectedSeatsIds = selectedSeats.map(
            (selectedSeat) => selectedSeat.id,
        );
        const viewMode = getViewMode();

        let tierToUse;
        let typeToUse;
        let canPick = true;
        let hasLockedSeats = false;
        let statusToUse;

        /*
        We can select multiple seats if:
            - None of the seats is locked
            - They all belong to the same tier
            - They are all of the same type (assigned seating or general admission)
            - They are all in the same status
        */
        const selectedSeatsIdsLength = selectedSeatsIds.length;

        for (
            let i = 0;
            !hasLockedSeats && canPick && i < selectedSeatsIdsLength;
            i++
        ) {
            const seatId = selectedSeatsIds[i];
            const seatTier = getTierFromId({ id: seatId });
            const seatLocked = isSeatLocked({ id: seatId });
            const seatType = getSeatType({ id: seatId });
            const unitStatus = getFullStatusFromId({ id: seatId });

            if (
                seatLocked ||
                (viewMode === VIEW_MODE_MANAGE_DASHBOARD &&
                    selectedSeatsIdsLength > 1 &&
                    unitStatus === STATUS_RESERVED)
            ) {
                canPick = false;
                hasLockedSeats = true;
            }

            // We set status, type and tier to the first id
            // and for multiple ids, we have to be consistent
            if (i === 0) {
                statusToUse = unitStatus;
                typeToUse = seatType;
                tierToUse = seatTier;
            } else {
                // If we find pickable seats in different statuses,
                // we reset the statusToUse to undefined.
                if (statusToUse && statusToUse !== unitStatus) {
                    statusToUse = undefined;
                }
                if (typeToUse !== seatType || tierToUse !== seatTier) {
                    canPick = false;
                }
            }
        }

        if (!canPick) {
            selectSeats([]);
            setErrorMessage({
                msg: gettext(
                    'All seats must be available and belong to the same tier for group picking',
                ),
            });

            return;
        }

        if (hasLockedSeats) {
            return;
        }

        // At this point,we only have to check on the type of the first ticket,
        // as canSelectSeats will not allow us to have mixed types
        const globals = model.get('globals');
        const tierVariants = getVariantsFromTier({ globals, tier: tierToUse });

        if (typeToUse === 'general_admission_section') {
            if (value || value === undefined) {
                if (
                    selectedSeatsIds.find(
                        (seatId) => seatId !== selectedSeatsIds[0],
                    )
                ) {
                    // For the case of general admission, this should never happen, as the UI should prevent us from
                    // selecting multiple sections for general admission
                    return setErrorMessage({
                        msg: gettext(
                            'Cannot add multiple general_admission_section ids to the cart',
                        ),
                    });
                }

                _addGATicket({ selectedSeats }, tierVariants);
            } else {
                _removeGATicket({ selectedSeats });
            }
        } else {
            let nextStatus = STATUS_REQUESTING;
            let { variantId: variantToUseId } =
                selectedSeats.find(
                    (selectedSeat) => !!selectedSeat.variantId,
                ) || {};

            if (!variantToUseId && tierVariants.length === 1) {
                variantToUseId = tierVariants[0].variantId;
            }

            if (value === undefined) {
                switch (statusToUse) {
                    // If all of the seats are carted, we release them
                    // otherwise, we request them
                    case STATUS_CARTED:
                        nextStatus = STATUS_RELEASING;
                        break;
                    default:
                }
            } else if (value) {
                nextStatus = STATUS_REQUESTING;
            } else {
                nextStatus = STATUS_RELEASING;
            }

            const ts = timestamp();
            const isSold = seatIsSold({ ids: selectedSeatsIds });
            const isTicketVisibleOrInPASMode = _isTicketVisibleOrInPASMode();

            // We only cart the tickets if
            // 1. There is only one variant AND
            // 2. The ticket is not sold AND
            // 3. The ticket is visible (floating dialog is being displayed) OR we are in PAS mode
            // OR if we are releasing.
            if (
                (variantToUseId && !isSold && isTicketVisibleOrInPASMode) ||
                nextStatus === STATUS_RELEASING
            ) {
                if (
                    nextStatus === STATUS_REQUESTING &&
                    maxReachedForVariant({
                        variantId: variantToUseId,
                        quantity: selectedSeatsIds.length,
                    })
                ) {
                    setErrorMessage({
                        msg: getMaxTicketError({ variantId: variantToUseId }),
                        transient: false,
                    });

                    return;
                }
                for (let i = 0; i < selectedSeatsIds.length; i++) {
                    const seatId = selectedSeatsIds[i];

                    const seatPath = `globals.cart.${seatId}`;

                    const currentStatus = model.get(`${seatPath}.status`);

                    if (nextStatus !== currentStatus) {
                        model.set(`${seatPath}.variant_id`, variantToUseId);
                        model.set(`${seatPath}.status`, nextStatus);
                        model.set(`${seatPath}.ts`, ts);
                    }
                }

                if (nextStatus === STATUS_REQUESTING) {
                    trackAnalyticsAction({ action: 'lockSeat' });
                }
                if (nextStatus === STATUS_RELEASING) {
                    trackAnalyticsAction({ action: 'releaseSeat' });
                }
                // We clear seat selection
                selectSeats([]);
                aShowTicketSelection({ visible: false });
                aSyncCart();
            } else {
                aShowTicketSelection({
                    ids: selectedSeatsIds,
                    visible: true,
                });
            }
        }
    };

    const notifyAppStarted = () => {
        cartServiceNotify({
            args: [],
            type: 'app:started',
        });
    };

    const removeCartItem = ({ seatId, variantId }) => {
        let variantIdToRemove = variantId;

        if (!variantIdToRemove) {
            variantIdToRemove = model.get(`globals.cart.${seatId}.variant_id`);
        }

        if (seatId) {
            pickSeats({
                selectedSeats: [
                    {
                        id: seatId,
                        variantId: variantIdToRemove,
                    },
                ],
                value: false,
            });
        }
    };

    const onTriggerRemoveCartItem = (payload) => {
        if (payload && payload.length) {
            const seatId = payload[0];

            removeCartItem({ seatId });
        }
    };

    const onTriggerUpdateAttendees = (args = []) => {
        const tickets = args[0];
        const gaNode = {};
        const cartNode = {};
        const ts = timestamp();

        if (tickets) {
            tickets.forEach((ticket = {}) => {
                const variantId = ticket.variantId;
                const ticketClassId = ticket.ticket_class_id;
                const unitId = (ticket.assignedUnit || {}).unitId;

                if (!unitId || !variantId) {
                    return;
                }

                if (unitId.includes(':')) {
                    cartNode[unitId] = {
                        status: STATUS_CARTED,
                        ts,
                        ticketClassId,
                        variant_id: variantId,
                    };
                } else {
                    const ticketsFromGA = gaNode[unitId] || [];

                    ticketsFromGA.push({
                        ts,
                        variant_id: variantId,
                    });
                    gaNode[unitId] = ticketsFromGA;
                }
            });

            model.set('globals.cartGA', gaNode);
            model.set('globals.cart', cartNode);
            aSyncCart();
        }
    };

    const onTriggerChangePromoCode = async (payload) => {
        // Let's clear the cart
        const cart = model.get('globals.cart') || {};
        const selectedIds = Object.keys(cart);
        const ts = timestamp();

        // We get all tickets which are in the cart, being requested or being released
        // and we set them all to releasing along with updating their ts
        selectedIds.forEach((unitId) => {
            const unitStatus = model.get(`globals.cart.${unitId}.status`);

            if (
                unitStatus === STATUS_CARTED ||
                unitStatus === STATUS_RELEASING ||
                unitStatus === STATUS_REQUESTING
            ) {
                model.set(`globals.cart.${unitId}.status`, STATUS_RELEASING);
                model.set(`globals.cart.${unitId}.ts`, ts);
            }
        });
        // We clear all our GA tickets
        model.set('globals.cartGA', {});
        model.set('globals.promoCode', payload);
        // We sync the cart so that seats become available
        await aSyncCart();
        // We set the promo code, which may change status
        await aSetPromoCode({ promoCode: payload });
        // We finally remove all seat selection. This is necessary so that
        // seats get their rightful state (A,P,etc) after the cart has been cleared
        // and all the status has been updated
        model.set('globals.cart', {});

        cartServiceNotify({
            args: { code: [payload] },
            type: 'promoCode:applied',
        });
    };

    const _getLabelsFromId = ({ rowId, sectionId, unitId }) => {
        let sectionLabel = sectionId;

        let rowLabel = rowId;

        let unitLabel = unitId;
        const sectionProperties = model.get(
            `objects.contents.${sectionId}.properties`,
        );

        if (sectionProperties) {
            sectionLabel = sectionProperties.l;
        }

        if (rowId) {
            const rowProperties = model.get(
                `objects.contents.${sectionId}.contents.${sectionId}-${rowId}.properties`,
            );

            if (rowProperties) {
                rowLabel = rowProperties.l;
            }
        }

        if (unitId) {
            let unitPath;

            if (rowId) {
                unitPath = `objects.contents.${sectionId}.contents.${sectionId}-${rowId}.chairs`;
            } else {
                unitPath = `objects.contents.${sectionId}.chairs`;
            }
            const chairs = model.get(unitPath);

            if (chairs && chairs.l) {
                unitLabel = chairs.l[+unitId - 1];
            }
        }

        return {
            rowLabel,
            sectionLabel,
            unitLabel,
        };
    };

    const getSeatInfoFromIds = ({ ids }) => {
        if (!ids || !ids.length) {
            return {};
        }

        const rowIds = {};
        const rowLabels = {};
        const rowTitles = {};
        const sectionIds = {};
        const sectionLabels = {};
        const sectionTitles = {};
        const unitIds = {};
        const unitLabels = {};
        const unitTitles = {};

        // If the map contains only one key, we return it
        // otherwise we return undefined
        const _getOneOrMulti = (map) => {
            const keys = Object.keys(map);

            return keys.length === 1 ? keys[0] : undefined;
        };

        for (let i = 0; i < ids.length; i++) {
            const id = ids[i];
            const parts = id.split(':');
            const pathPart = parts[0] || '';
            const unitId = parts[1] || '';
            const pathItems = pathPart.split('-');
            const sectionId = pathItems[0];
            const rowId = pathItems[1] || '';

            const properties =
                model.get(`objects.contents.${sectionId}.properties`) || {};
            const {
                title1: sectionTitle = '',
                title2: rowTitle = '',
                title3: unitTitle = '',
            } = properties;

            const { rowLabel, sectionLabel, unitLabel } = _getLabelsFromId({
                rowId,
                sectionId,
                unitId,
            });

            rowIds[rowId] = true;
            rowLabels[rowLabel] = true;
            rowTitles[rowTitle] = true;
            sectionIds[sectionId] = true;
            sectionLabels[sectionLabel] = true;
            sectionTitles[sectionTitle] = true;
            unitLabels[unitLabel] = true;
            unitIds[unitId] = true;
            unitTitles[unitTitle] = true;
        }

        return {
            ids,
            rowId: _getOneOrMulti(rowIds),
            rowLabel: _getOneOrMulti(rowLabels),
            rowTitle: _getOneOrMulti(rowTitles),
            sectionId: _getOneOrMulti(sectionIds),
            sectionLabel: _getOneOrMulti(sectionLabels),
            sectionTitle: _getOneOrMulti(sectionTitles),
            unitLabel: _getOneOrMulti(unitLabels),
            unitId: _getOneOrMulti(unitIds),
            unitTitle: _getOneOrMulti(unitTitles),
        };
    };

    const getColorFromTier = (tier) => model.get(`tiers.${tier}.color`);

    const getNameFromTier = (tier) => model.get(`tiers.${tier}.name`);

    const getAttendees = () => model.get('globals.attendees');

    const getTicketClasses = () => model.get('globals.ticketClasses') || [];

    const getAddons = () => model.get('globals.addons') || {};

    const getEventCurrency = () => model.get('globals.eventData.currency');

    const _getSummaryLines = memoize((attendees) => {
        let summaryLines = [];
        const auxDict = {};

        if (attendees && attendees.length) {
            attendees.forEach((attendee) => {
                const {
                    id: attendeeId,
                    ticket_class_id: ticketClassId,
                    quantity,
                    costs: {
                        gross: { value: grossValue = 0, currency = '' } = {},
                    } = {},
                    assigned_unit: { unit_id: unitId } = {},
                    ticket_class_name: ticketClassName,
                } = attendee;
                const selectedTicketClass = auxDict[ticketClassId];

                if (!selectedTicketClass) {
                    let color;

                    if (unitId) {
                        const tier = getTierFromId({ id: unitId });

                        color = getColorFromTier(tier);
                    }

                    auxDict[ticketClassId] = {
                        attendeeId,
                        ticketClassId,
                        unitId,
                        grossValue,
                        ticketClassName,
                        currency,
                        quantity,
                        color,
                        ticketPrice: grossValue,
                    };
                } else {
                    selectedTicketClass.quantity += quantity;
                    selectedTicketClass.grossValue += grossValue;
                }
            });

            summaryLines = Object.keys(auxDict).map((key) => ({
                ticketClassId: auxDict[key].ticketClassId,
                attendeeId: auxDict[key].attendeeId,
                unitId: auxDict[key].unitId,
                name: auxDict[key].ticketClassName,
                quantity: auxDict[key].quantity.toString(),
                totalPrice: formatMajorMoney(
                    auxDict[key].grossValue,
                    auxDict[key].currency,
                ),
                ticketPriceNumber: formatNumber(auxDict[key].ticketPrice / 100),
                tier: auxDict[key].color,
            }));
        }

        return summaryLines;
    });

    // Please note use of memoize here so that we only perform
    // the calculations when the ticket classes or the addons change.
    // We can safely do this as we are using simmut and therefore
    // ticket classes and addons are immutable.
    const _getAddonsInfo = memoize((addonsMap, ticketClasses) => {
        let addonsInfo = [];

        if (ticketClasses && ticketClasses.length) {
            addonsInfo = ticketClasses.reduce((accum, ticketClass) => {
                if (ticketClass.category === TICKET_CLASS_CATEGORY_ADDON) {
                    const {
                        variant_id: variantId,
                        name,
                        cost: { display: costDisplay } = {},
                    } = ticketClass;

                    const addon = addonsMap[variantId] || {};

                    accum.push({
                        id: variantId,
                        name,
                        price: costDisplay,
                        quantity: addon.qty || 0,
                    });
                }

                return accum;
            }, []);
        }

        return addonsInfo;
    });

    // Please note use of memoize here so that we only perform
    // the calculations when the attendees change.
    // We can safely do this as we are using simmut and therefore
    // attendees are immutable.
    const _getOrderTicketsInfo = memoize((attendees) => {
        let tickets = [];

        if (attendees && attendees.length) {
            tickets = attendees.reduce((accum, attendee) => {
                const {
                    assigned_unit: { unit_id: unitId } = {},
                    variant_id: variantId,
                    ticket_class_name: ticketClassName,
                } = attendee;

                if (unitId) {
                    const tier = getTierFromId({ id: unitId });
                    const color = getColorFromTier(tier);
                    const tierName = getNameFromTier(tier);
                    const { sectionLabel, rowLabel, unitLabel } =
                        getSeatInfoFromIds({ ids: [unitId] });

                    accum.push({
                        id: unitId,
                        variantId,
                        variantName: ticketClassName.replace(
                            tierName + ' ',
                            '',
                        ),
                        section: sectionLabel,
                        row: rowLabel,
                        seat: unitLabel,
                        tier: color,
                    });
                }

                return accum;
            }, []);
        }

        return tickets;
    });

    const _getTotalPriceInfo = memoize((attendees) => {
        let orderCurrency = '';
        let totalValue = 0;

        if (attendees && attendees.length) {
            attendees.forEach((attendee) => {
                const {
                    costs: {
                        gross: { value: grossValue = 0, currency = '' } = {},
                    } = {},
                } = attendee;

                orderCurrency = currency;
                totalValue += grossValue;
            });
        }

        return {
            price: formatMajorMoney(totalValue, orderCurrency),
            currency: orderCurrency,
        };
    });

    const getOrderInfo = () => {
        const attendees = getAttendees();
        const ticketClasses = getTicketClasses();
        const addons = getAddons();

        return {
            addons: _getAddonsInfo(addons, ticketClasses),
            summaryLines: _getSummaryLines(attendees),
            tickets: _getOrderTicketsInfo(attendees),
            totalPriceInfo: _getTotalPriceInfo(attendees),
        };
    };

    const _getGAHeldSubsections = (id) => {
        const holds = model.get('holds') || {};
        const sectionIdsWithHolds = Object.keys(holds.ids);
        const re = new RegExp(`${id}(-\\d){1}`);
        const subsectionsFromSelectedSection = sectionIdsWithHolds.filter(
            (subsectionId) => re.test(subsectionId),
        );
        return subsectionsFromSelectedSection;
    };

    const _hasGASubSectionWithHolds = ({ id }) => {
        const sectionStatus = model.get(`status.${id}.S`) || {};

        const heldSubsections = _getGAHeldSubsections(id) || [];

        const statusCollection = heldSubsections.reduce(
            (previousValue, subsection) => {
                const subSectionStatus = model.get(`status.${subsection}.S`);
                if (subSectionStatus) {
                    return [...previousValue, subSectionStatus];
                }
                return previousValue;
            },
            [sectionStatus],
        );

        return !!statusCollection.find((status) => status.P);
    };

    // Giving a list of seats(id)ds, checks if any in the list has a
    // RESERVED status.
    const seatsHaveHolds = ({ ids = [] }) => {
        const seatsWithHolds = ids.find((id) => {
            const isReservedSeatingUnit = _isReservedSeatingUnit(id);
            const fullStatus = getFullStatusFromId({ id });
            return isReservedSeatingUnit
                ? fullStatus === STATUS_PROTECTED
                : fullStatus === STATUS_PROTECTED ||
                      _hasGASubSectionWithHolds({ id });
        });
        return !!seatsWithHolds;
    };

    const setAddonQuantity = (id, value) => {
        const valueToUse = isNaN(value) ? 0 : +value;

        model.set(`globals.addons.${id}.qty`, valueToUse);
        model.set(`globals.addons.${id}.ts`, Date.now());
    };

    const _setTicketClassGross = (ticketClassId, gross) => {
        const ticketIndex = getTicketClasses().findIndex(
            ({ id }) => id === ticketClassId,
        );

        if (ticketIndex < 0) {
            return;
        }

        model.set(`globals.ticketClasses.${ticketIndex}.gross`, gross);
    };

    const setOrderSummaryTicketPrice = (ticketClassId, value) => {
        if (isNaN(value)) {
            return;
        }

        const valueToUse = Math.round(+value * 100);
        const currency = getEventCurrency();
        const gross = { currency, value: valueToUse };

        _setTicketClassGross(ticketClassId, gross);
    };

    const _calculateTotalUnits = (status, sectionId) => {
        let totalUnits = 0;

        Object.keys(status).forEach((key) => {
            if (
                !sectionId ||
                key === sectionId ||
                key.startsWith(`${sectionId}-`)
            ) {
                if (status[key].s) {
                    totalUnits += status[key].s.filter(
                        (seatStatus) => !!seatStatus,
                    ).length;
                } else {
                    Object.keys(status[key].S).forEach((statusKey) => {
                        totalUnits += status[key].S[statusKey];
                    });
                }
            }
        });

        return totalUnits;
    };

    const _calculateAvailableUnits = (cart, cartGA, status, sectionId) => {
        let availableUnits = 0;

        // We sum all the available units in each reserved or GA section.
        Object.keys(status).forEach((key) => {
            if (
                !sectionId ||
                key === sectionId ||
                key.startsWith(`${sectionId}-`)
            ) {
                if (status[key].s) {
                    availableUnits += status[key].s.filter(
                        (seatStatus) => seatStatus === STATUS_AVAILABLE,
                    ).length;
                    // For GA sections we need to discard holds.
                    // GA sections holds ids follow this pattern: `${sectionId}-${holdId}.
                } else if (status[key].S) {
                    const {
                        [STATUS_AVAILABLE]: statusAvailableQuantity = 0,
                        [STATUS_PROTECTED]: statusProtectedQuantity = 0,
                    } = status[key].S;

                    availableUnits +=
                        statusAvailableQuantity + statusProtectedQuantity;
                }
            }
        });

        // We count the not available units in the cart
        const quantityInCart = Object.keys(cart).reduce((quantity, item) => {
            if (!sectionId || sectionId === item) {
                if (cart[item].status !== STATUS_AVAILABLE) {
                    return quantity + 1;
                }
            }

            return quantity;
        }, 0);

        // We count the not available units in the GA cart
        const quantityInCartGA = Object.keys(cartGA).reduce(
            (quantity, item) => {
                if (!sectionId || sectionId === item) {
                    quantity += cartGA[item].length;
                }

                return quantity;
            },
            0,
        );

        // We return the available units minus the quantity in the cart and the
        // quantity in the GA cart.
        return availableUnits - quantityInCart - quantityInCartGA;
    };

    const _getTotalUnits = memoize((status) => _calculateTotalUnits(status));

    const _getSectionTotalUnits = memoize((status, sectionId) =>
        _calculateTotalUnits(status, sectionId),
    );

    const _getAvailableUnits = memoize((cart, cartGA, status) =>
        _calculateAvailableUnits(cart, cartGA, status),
    );

    const _getSectionAvailableUnits = memoize(
        (cart, cartGA, status, sectionId) =>
            _calculateAvailableUnits(cart, cartGA, status, sectionId),
    );

    const getTotalUnitsInfo = () => {
        const cartGA = model.get('globals.cartGA') || {};
        const cart = model.get('globals.cart') || {};
        const status = model.get('status') || {};

        return {
            available: _getAvailableUnits(cart, cartGA, status),
            total: _getTotalUnits(status),
        };
    };

    const _getSectionAvailableHoldsUnits = memoize((status, sectionId) =>
        Object.keys(status).reduce((availableHoldsUnits, key) => {
            if (key.startsWith(`${sectionId}-`)) {
                return (availableHoldsUnits +=
                    status[key].S[STATUS_PROTECTED] || 0);
            }
            return availableHoldsUnits;
        }, 0),
    );

    const _getSectionTotalHoldsUnits = memoize((holdsInfo, sectionId) =>
        Object.keys(holdsInfo).reduce(
            (acc, holdId) =>
                acc +
                Object.keys(holdsInfo[holdId]).reduce(
                    (totalHoldsUnits, key) => {
                        if (key.startsWith(`${sectionId}-`)) {
                            return (totalHoldsUnits +=
                                holdsInfo[holdId][key].totalHeldQuantity || 0);
                        }
                        return totalHoldsUnits;
                    },
                    0,
                ),
            0,
        ),
    );

    const _getSectionSoldUnits = memoize((status, sectionId) =>
        Object.keys(status).reduce((soldUnits, key) => {
            if (key === sectionId || key.startsWith(`${sectionId}-`)) {
                return (soldUnits += status[key].S[STATUS_RESERVED] || 0);
            }
            return soldUnits;
        }, 0),
    );

    const getGASectionTotalUnitsInfo = (sectionId) => {
        let available = 0;
        let total = 0;
        let availableHolds = 0;
        let totalHolds = 0;
        let sold = 0;
        const cartGA = model.get('globals.cartGA') || {};
        const cart = model.get('globals.cart') || {};
        const status = model.get('status') || {};
        const holdsInfo = model.get('holds.info') || {};

        if (sectionId) {
            available = _getSectionAvailableUnits(
                cart,
                cartGA,
                status,
                sectionId,
            );
            total = _getSectionTotalUnits(status, sectionId);
            availableHolds = _getSectionAvailableHoldsUnits(status, sectionId);
            totalHolds = _getSectionTotalHoldsUnits(holdsInfo, sectionId);
            sold = _getSectionSoldUnits(status, sectionId);
        }

        return {
            available,
            total,
            availableHolds,
            totalHolds,
            sold,
        };
    };

    const seatsBelongToGASection = ({ ids = [] } = {}) =>
        ids.length > 0 &&
        !ids.find((id) => getSeatType({ id }) !== 'general_admission_section');

    // This function allow us to reset the selected tickets data for the GA section
    const resetSelectedGATickets = () =>
        model.set('globals.selectedGATickets.quantities', {});

    const getGASectionVariants = ({ sectionId, variants }) => {
        const variantsWithQuantity = variants.map((variant) => ({
            ...variant,
            quantity:
                model.get(
                    `globals.selectedGATickets.quantities.${sectionId}.${variant.variantId}`,
                ) || '',
        }));

        return variantsWithQuantity;
    };

    const setSelectedGASectionVariantQuantity = ({
        sectionId,
        variantId,
        quantity,
    }) => {
        const parsedQuantity = parseInt(quantity) || 0;

        model.set(
            `globals.selectedGATickets.quantities.${sectionId}.${variantId}`,
            parsedQuantity,
        );
    };

    const handleSelectGATickets = ({ sectionId }) => {
        const sectionQuantities =
            model.get(`globals.selectedGATickets.quantities.${sectionId}`) ||
            {};
        let selectedSeats = [];

        if (sectionQuantities) {
            selectedSeats = Object.keys(sectionQuantities).reduce(
                (acc, variantId) => {
                    const variantQuantity = model.get(
                        `globals.selectedGATickets.quantities.${sectionId}.${variantId}`,
                    );

                    if (variantQuantity) {
                        const quantitySelected = variantQuantity;

                        for (let i = 0; i < quantitySelected; i++) {
                            acc.push({
                                id: sectionId,
                                variantId,
                            });
                        }
                    }

                    return acc;
                },
                [],
            );
        }

        if (selectedSeats.length) {
            pickSeats({
                selectedSeats: selectedSeats,
                value: true,
            });
        }
    };

    const _calculateHasSelectedGATickets = memoize(
        (selectedGATicketsQuantities) => {
            const hasSelectedGATickets = !!Object.keys(
                selectedGATicketsQuantities,
            ).find((sectionId) =>
                Object.keys(
                    model.get(
                        `globals.selectedGATickets.quantities.${sectionId}`,
                    ),
                ).find(
                    (variantId) =>
                        model.get(
                            `globals.selectedGATickets.quantities.${sectionId}.${variantId}`,
                        ) > 0,
                ),
            );

            return hasSelectedGATickets;
        },
    );

    const hasSelectedGATickets = () => {
        const selectedGATicketsQuantities =
            model.get('globals.selectedGATickets.quantities') || {};

        return _calculateHasSelectedGATickets(selectedGATicketsQuantities);
    };

    const _checkHasItemsCarted = memoize(
        ({ cart = {}, cartGA = {}, addons = {} }) => {
            return !!(
                Object.keys(cart).find(
                    (key) => cart[key].status === STATUS_CARTED,
                ) ||
                Object.keys(cartGA).find((key) => cartGA[key].length > 0) ||
                Object.keys(addons).find((key) => addons[key].qty > 0)
            );
        },
    );

    const hasItemsCarted = () => {
        const cart = model.get('globals.cart');
        const cartGA = model.get('globals.cartGA');
        const addons = model.get('globals.addons');

        return _checkHasItemsCarted({
            cart,
            cartGA,
            addons,
        });
    };

    const setPaymentInstruction = async ({ paymentStatus }) => {
        model.set('globals.orderExtraPaymentInfo.paymentStatus', undefined);
        const { cartId } = getEventIdAndCartId();
        const paymentAmount = getOrderAmountInfo();
        try {
            const orderUpdated = await eventServiceASetPaymentInstruction({
                cartId: cartId,
                paymentStatus,
                paymentAmount,
            });
            setOrderData(orderUpdated);
        } catch (err) {
            setErrorMessage({
                msg: gettext(
                    'There was an error setting the payment method. Please try again later.',
                ),
            });
        }
        model.set('globals.orderExtraPaymentInfo.paymentStatus', paymentStatus);
    };

    const getPaymentInstruction = () =>
        model.get('globals.orderExtraPaymentInfo.paymentStatus');

    const getOrderPriceInfo = () => {
        let currency = '';
        let price = '';
        const orderPriceInfo = model.get('globals.order.costs.display_price');

        if (orderPriceInfo) {
            currency = orderPriceInfo.currency;
            price = orderPriceInfo.display;
        }

        return {
            currency,
            price,
        };
    };

    const getOrderAmountInfo = () => {
        const orderAmountInfo = model.get('globals.order.costs.display_price');

        return {
            currency: orderAmountInfo?.currency || '',
            value: orderAmountInfo?.value || '',
        };
    };

    const getManageHoldsModalVisibility = () =>
        model.get('globals.manageHoldsModalIsShowing');

    const setManageHoldsModalVisibility = (value) =>
        model.set('globals.manageHoldsModalIsShowing', value);

    const _matchHoldsInfoWithSection = ({ sectionId, holdsIds, holdsInfo }) => {
        const sectionHoldsIds = Object.keys(holdsIds).reduce((acc, key) => {
            if (key.startsWith(`${sectionId}-`)) {
                acc[`H${holdsIds[key]}`] = key;
            }

            return acc;
        }, {});

        const sectionHoldsInfo = holdsInfo.reduce((acc, holdInfo) => {
            const { id: holdId, quantity_total: quantityTotal } = holdInfo;

            if (sectionHoldsIds[holdId]) {
                const subSectionId = sectionHoldsIds[holdId];
                const totalHeldUnitsInSection = model.get(
                    `holds.info.${holdId.slice(
                        1,
                        holdId.length,
                    )}.${subSectionId}.totalHeldQuantity`,
                );
                const soldHeldQuantity = model.get(
                    `holds.info.${holdId.slice(
                        1,
                        holdId.length,
                    )}.${subSectionId}.soldHeldQuantity`,
                );
                acc.push({
                    ...holdInfo,
                    updatedQuantity: quantityTotal,
                    unitId: sectionHoldsIds[holdId],
                    totalHeldUnitsInSection,
                    soldHeldQuantity,
                });
            }

            return acc;
        }, []);

        return sectionHoldsInfo;
    };

    const showManageHoldsModal = async ({ sectionId }) => {
        const { eventId } = getEventIdAndCartId();
        const holdsResult = await eventServiceARetrieveHoldsData({ eventId });

        if (holdsResult && holdsResult.holds) {
            const holdsIds = model.get('holds.ids') || {};

            model.set('globals.sectionHoldsInfo', {
                holds: _matchHoldsInfoWithSection({
                    sectionId,
                    holdsIds,
                    holdsInfo: holdsResult.holds,
                }),
                isDirty: false,
                sectionId,
            });
            setManageHoldsModalVisibility(true);
        } else {
            setErrorMessage({
                msg: gettext(
                    'There was an error retrieving the info. Please try again later.',
                ),
            });
            return;
        }
    };

    const getSectionHoldsInfo = () => {
        const sectionHoldsInfo = model.get('globals.sectionHoldsInfo');
        return sectionHoldsInfo;
    };
    const setSectionHoldQuantity = ({ holdId, quantity }) => {
        const { holds = [] } = getSectionHoldsInfo() || {};
        let isDirty = false;
        const updatedHolds = holds.map((hold) => {
            const { id, quantity_total: quantityTotal } = hold;

            if (id === holdId && !isNaN(quantity)) {
                const quantityHasChanged = +quantity !== quantityTotal;
                const updatedHold = {
                    ...hold,
                    updatedQuantity: +quantity,
                    isDirty: quantityHasChanged,
                };

                if (quantityHasChanged) {
                    isDirty = true;
                }

                return updatedHold;
            }

            return hold;
        });

        model.set('globals.sectionHoldsInfo.holds', updatedHolds);
        model.set('globals.sectionHoldsInfo.isDirty', isDirty);
    };

    // NOTE: In order to update the holds, we also need to update the parent section capacity
    // to keep consistency.
    const _getHoldsParsedSpecUnitsToUpdate = ({ sectionId, holds = [] }) => {
        const parsedSpecUnits = [];
        const {
            [STATUS_AVAILABLE]: sectionAvailableQuantity = 0,
            [STATUS_RESERVED]: sectionSoldQuantity = 0,
            [STATUS_LOCKED]: sectionLockedQuantity = 0,
        } = model.get(`status.${sectionId}.S`);

        // We don't update the status when we cart items so we must include
        // the quantities in the GA cart.
        const cartGA = model.get('globals.cartGA') || {};
        const sectionInCartQuantity = Object.keys(cartGA).reduce(
            (quantity, item) => {
                if (!sectionId || sectionId === item) {
                    quantity += cartGA[item].length;
                }

                return quantity;
            },
            0,
        );
        let updatedSectionQuantity =
            sectionAvailableQuantity +
            sectionSoldQuantity +
            sectionLockedQuantity +
            sectionInCartQuantity;

        holds.forEach((hold) => {
            const { unitId, updatedQuantity, isDirty } = hold;

            if (isDirty) {
                const { [STATUS_PROTECTED]: sectionProtectedQuantity = 0 } =
                    model.get(`status.${unitId}.S`);

                parsedSpecUnits.push({
                    id: unitId,
                    quantity: updatedQuantity,
                });
                updatedSectionQuantity +=
                    sectionProtectedQuantity - updatedQuantity;
            }
        });

        parsedSpecUnits.push({
            id: sectionId,
            quantity: updatedSectionQuantity,
        });

        return parsedSpecUnits;
    };

    const saveSectionHolds = async () => {
        const { eventId } = getEventIdAndCartId();
        const { sectionId, holds = [] } = getSectionHoldsInfo() || {};

        try {
            await eventServiceAUpdateEventHolds({
                eventId,
                specUnits: _getHoldsParsedSpecUnitsToUpdate({
                    holds,
                    sectionId,
                }),
            });
            setSuccessMessage({
                msg: gettext('Holds updated'),
                isTransient: true,
            });
        } catch (err) {
            setErrorMessage({
                msg: gettext(
                    'There was an error updating the holds. Please try again later.',
                ),
            });
        }

        setManageHoldsModalVisibility(false);
    };

    const setCheckoutWidgetVisibility = (value) =>
        model.set('globals.checkoutWidgetIsShowing', value);

    const getCheckoutWidgetVisibility = () =>
        model.get('globals.checkoutWidgetIsShowing');

    const savePaymentInstruction = async () => {
        const { cartId } = getEventIdAndCartId();
        const paymentStatus = getPaymentInstruction();
        const paymentAmount = getOrderAmountInfo();

        if (cartId && paymentStatus) {
            try {
                await eventServiceASetPaymentInstruction({
                    cartId: cartId,
                    paymentStatus,
                    paymentAmount,
                });
                setCheckoutWidgetVisibility(true);
                setCheckoutModalVisibility(false);
            } catch (err) {
                setErrorMessage({
                    msg: gettext(
                        'There was an error setting the payment method. Please try again later.',
                    ),
                });
            }
        }
    };

    const getPrintDialogVisibility = () =>
        model.get('globals.printDialogIsShowing');

    const togglePrintDialogVisibility = () => {
        model.set('globals.legendDialogIsShowing', false);
        model.set('globals.printDialogIsShowing', !getPrintDialogVisibility());
    };

    const hidePrintDialog = () =>
        model.set('globals.printDialogIsShowing', false);

    const seatIsSold = ({ ids = [] }) => {
        const fullStatus = getFullStatusFromId({ id: ids[0] });
        return ids.length === 1 && fullStatus === STATUS_RESERVED;
    };

    const getSoldSeatInfoFetched = () =>
        model.get('globals.soldSeatInfoFetched');

    const getSoldSeatInfo = () => model.get('globals.soldSeatInfo');

    const _fetchSoldSeatInfo = async (seatId, eventId) => {
        const soldSeatInfo = await eventServiceARetrieveSoldSeatInfo({
            seatId,
            eventId,
        });

        return soldSeatInfo;
    };

    const fetchSoldSeatInfo = memoize((seatId, eventId) =>
        _fetchSoldSeatInfo(seatId, eventId),
    );

    const setSoldSeatInfo = async ({ ids = [] }) => {
        const { eventId } = getEventIdAndCartId();

        model.set('globals.soldSeatInfoFetched', false);

        // NOTE: We only support showing one sold seat at a time
        if (ids.length !== 1) {
            model.set('globals.soldSeatInfo', undefined);

            return;
        }

        const selectedSeatId = ids[0];
        const { seatId } = getSoldSeatInfo() || {};

        if (selectedSeatId && seatId !== selectedSeatId) {
            try {
                const soldSeatInfo = await fetchSoldSeatInfo(
                    selectedSeatId,
                    eventId,
                );

                model.set('globals.soldSeatInfo', {
                    seatId: selectedSeatId,
                    ...soldSeatInfo,
                });
            } catch (err) {
                setErrorMessage({
                    msg: gettext(
                        'There was an error retrieving the info. Please try again later.',
                    ),
                });
            }
        }

        model.set('globals.soldSeatInfoFetched', true);
    };

    const getHeldSeatIsReleasing = () =>
        model.get('globals.heldSeatIsReleasing');

    const releaseHold = async ({ id }) => {
        model.set('globals.heldSeatIsReleasing', true);

        const { eventId } = getEventIdAndCartId();

        try {
            const allHolds = { ...model.get('holds.ids') };

            await eventServiceAReleaseHold({
                eventId,
                seatId: id,
            });

            delete allHolds[id];
            model.set('holds.ids', allHolds);

            setSuccessMessage({
                msg: gettext('Hold released'),
                isTransient: true,
            });
        } catch (err) {
            setErrorMessage({
                msg: gettext(
                    'There was an error releasing the hold. Please try again later.',
                ),
            });
        }

        model.set('globals.heldSeatIsReleasing', false);
    };

    const init = () => {
        cartServiceOn('triggerRemoveCartItem', onTriggerRemoveCartItem);
        cartServiceOn('triggerUpdateAttendees', onTriggerUpdateAttendees);
        cartServiceOn('triggerChangePromoCode', onTriggerChangePromoCode);
    };

    const dispose = () => {
        cartServiceOff('triggerRemoveCartItem', onTriggerRemoveCartItem);
        cartServiceOff('triggerUpdateAttendees', onTriggerUpdateAttendees);
        cartServiceOff('triggerChangePromoCode', onTriggerChangePromoCode);
    };

    init();

    return {
        aLookAtAttendees,
        aSyncCart,
        dispose,
        getAddons,
        getCheckoutModalVisibility,
        getCheckoutWidgetVisibility,
        getColorFromTier,
        getFullStatusFromId,
        getGASectionTotalUnitsInfo,
        getGASectionVariants,
        getHeldSeatIsReleasing,
        getLegendDialogVisibility,
        getManageHoldsModalVisibility,
        getNameFromTier,
        getOrderInfo,
        getOrderPriceInfo,
        getOrderAmountInfo,
        getPaymentInstruction,
        getPrintDialogVisibility,
        getSeatInfoFromIds,
        getSeatType,
        getSectionHoldsInfo,
        getSoldSeatInfo,
        getSoldSeatInfoFetched,
        getSyncCartStatus,
        getTotalUnitsInfo,
        handleSelectGATickets,
        hasItemsCarted,
        hasSelectedGATickets,
        hideLegendDialog,
        hidePrintDialog,
        isCartExpired,
        isSeatLocked,
        maxReachedForVariant,
        notifyAppStarted,
        onTriggerChangePromoCode,
        onTriggerUpdateAttendees,
        pickSeats,
        releaseHold,
        removeCartItem,
        resetSelectedGATickets,
        savePaymentInstruction,
        saveSectionHolds,
        seatIsSold,
        seatsBelongToGASection,
        seatsHaveHolds,
        setAddonQuantity,
        setOrderSummaryTicketPrice,
        setCartFromAttendees,
        setCheckoutModalVisibility,
        setCheckoutWidgetVisibility,
        setManageHoldsModalVisibility,
        setPaymentInstruction,
        setSectionHoldQuantity,
        setSelectedGASectionVariantQuantity,
        setSoldSeatInfo,
        setSyncCartStatus,
        showManageHoldsModal,
        toggleLegendDialogVisibility,
        togglePrintDialogVisibility,
    };
};

export default pasManagerFactory;
